import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Zap, Sparkles, Image as ImageIcon, FileText, Megaphone, Check,
  Loader2, PlayCircle, Hourglass, ArrowRight, CalendarClock,
} from "lucide-react";
import { Link } from "react-router-dom";

const POLL_MS = 4000;

export default function PowerHour() {
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [seeds, setSeeds] = useState("");
  const [starting, setStarting] = useState(false);
  const [scheduleDays, setScheduleDays] = useState(30);
  const [scheduling, setScheduling] = useState(false);
  const [publishingBlogs, setPublishingBlogs] = useState(false);
  const pollRef = useRef(null);

  const loadJobs = async () => {
    const { data } = await api.get("/power-hour/jobs");
    setHistory(data || []);
    const running = (data || []).find((j) => j.status === "running");
    setJob(running || (data || [])[0] || null);
  };

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    if (job && job.status === "running") {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/power-hour/jobs/${job.id}`);
          setJob(data);
          if (data.status !== "running") {
            clearInterval(pollRef.current);
            await loadJobs();
            if (data.status === "completed") {
              toast.success("Power Hour complete! Outputs are queued for approval.");
            }
          }
        } catch {/* ignore */}
      }, POLL_MS);
      return () => clearInterval(pollRef.current);
    }
  }, [job?.id, job?.status]);

  const start = async () => {
    setStarting(true);
    try {
      const seed_topics = seeds.split("\n").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/power-hour/start", {
        seed_topics: seed_topics.length ? seed_topics : null,
      });
      setJob(data.job);
      if (data.already_running) {
        toast.message("A Power Hour is already running.");
      } else {
        toast.success("⚡ Power Hour started — generating 30 posts + 5 blogs + 10 images + 1 campaign in the background.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Power Hour");
    } finally {
      setStarting(false);
    }
  };

  const autoSchedule = async () => {
    setScheduling(true);
    try {
      const { data } = await api.post("/power-hour/auto-schedule", {
        job_id: job.id, days: scheduleDays,
      });
      toast.success(`${data.scheduled} posts auto-scheduled across ${data.days} days.`);
      await loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-schedule failed");
    } finally {
      setScheduling(false);
    }
  };

  const publishBlogs = async () => {
    setPublishingBlogs(true);
    try {
      const { data } = await api.post("/blog/publish-power-hour", { power_hour_job_id: job.id });
      toast.success(`${data.published} blog post(s) published live`);
      // Mark the job so the button disables next time
      setJob({ ...job, blogs_published: true, blogs_published_count: data.published });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishingBlogs(false);
    }
  };

  const progress = job?.progress || { posts: 0, blogs: 0, images: 0, campaign: 0 };
  const targets = job?.targets || { posts: 30, blogs: 5, images: 10, campaigns: 1 };
  const total = (targets.posts || 0) + (targets.blogs || 0) + (targets.images || 0) + (targets.campaigns || 0);
  const done = (progress.posts || 0) + (progress.blogs || 0) + (progress.images || 0) + (progress.campaign || 0);
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;

  return (
    <div className="space-y-8" data-testid="power-hour-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 inline-flex items-center gap-2">
          <Zap className="h-3.5 w-3.5" /> One-click batch generator
        </div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">⚡ Power Hour</h1>
        <p className="text-white/50 mt-3 max-w-2xl text-sm">
          Hit the button. In ~60 minutes the AI delivers a full month of content — 30 social posts, 5 SEO blog posts, 10 brand graphics, and 1 lead-gen campaign — all queued for your approval.
        </p>
      </header>

      <section className="grid md:grid-cols-4 gap-3" data-testid="power-hour-stats">
        <StatCard icon={Sparkles} label="Social posts" value={`${progress.posts}/${targets.posts}`} accent="indigo" />
        <StatCard icon={FileText} label="SEO blogs" value={`${progress.blogs}/${targets.blogs}`} accent="violet" />
        <StatCard icon={ImageIcon} label="Brand images" value={`${progress.images}/${targets.images}`} accent="purple" />
        <StatCard icon={Megaphone} label="Campaigns" value={`${progress.campaign}/${targets.campaigns}`} accent="fuchsia" />
      </section>

      {job && job.status === "running" && (
        <div className="glass rounded-2xl p-6 border border-indigo-500/30" data-testid="power-hour-progress">
          <div className="flex items-center gap-3 mb-3">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />
            <span className="text-sm">Power Hour in progress — {pct}% done</span>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full brand-gradient" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-xs text-white/40 mt-2 font-mono">
            Started {new Date(job.started_at).toLocaleString()}
          </div>
        </div>
      )}

      <section className="glass rounded-2xl p-6 lg:p-8">
        <h3 className="font-heading text-xl font-black mb-3">Customize seed topics (optional)</h3>
        <p className="text-xs text-white/40 mb-3">One topic per line — leave blank to let the AI decide from your business profile.</p>
        <textarea
          rows={5}
          value={seeds}
          onChange={(e) => setSeeds(e.target.value)}
          placeholder="e.g.
Top 5 tips for first-time customers
How we deliver in 30 minutes
Why our quality is unbeatable"
          data-testid="power-hour-seeds-input"
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
        />
        <button
          onClick={start}
          disabled={starting || (job && job.status === "running")}
          data-testid="power-hour-start-btn"
          className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl brand-gradient text-sm font-bold hover:opacity-90 disabled:opacity-50"
        >
          {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
          {job && job.status === "running" ? "Already running…" : "Start Power Hour"}
        </button>
      </section>

      {job && job.status === "completed" && (
        <section className="glass rounded-2xl p-6 border border-violet-500/30" data-testid="publish-blogs-card">
          <div className="flex items-start gap-4 flex-wrap">
            <FileText className="h-6 w-6 text-violet-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-lg font-black">Publish blog posts as live pages</h3>
              <p className="text-sm text-white/60 mt-1">
                Push the 5 SEO blogs from this Power Hour batch to your public blog at <code className="text-violet-300">/blog</code>. Each gets a clean slug and is indexable.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  onClick={publishBlogs}
                  disabled={publishingBlogs || job.blogs_published}
                  data-testid="publish-blogs-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                >
                  {publishingBlogs ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                  {job.blogs_published ? `Already published (${job.blogs_published_count || 0})` : "Publish all blogs"}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {job && job.status === "completed" && (
        <section className="glass rounded-2xl p-6 border border-indigo-500/30" data-testid="auto-schedule-card">
          <div className="flex items-start gap-4 flex-wrap">
            <CalendarClock className="h-6 w-6 text-indigo-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-lg font-black">Auto-schedule across 30 days</h3>
              <p className="text-sm text-white/60 mt-1">
                Distribute every Power Hour post across the next 30 days at optimal per-platform posting times — one click, one full month of marketing scheduled.
                {job.auto_scheduled && <span className="ml-2 inline-flex items-center gap-1 text-emerald-300 text-xs"><Check className="h-3 w-3" /> Scheduled ({job.auto_scheduled_count || 0} posts)</span>}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <label className="text-xs text-white/40 uppercase tracking-[0.2em]">Window</label>
                <select
                  value={scheduleDays}
                  onChange={(e) => setScheduleDays(parseInt(e.target.value, 10))}
                  data-testid="auto-schedule-days"
                  className="bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs font-mono"
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                </select>
                <button
                  onClick={autoSchedule}
                  disabled={scheduling || job.auto_scheduled}
                  data-testid="auto-schedule-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                >
                  {scheduling ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
                  {job.auto_scheduled ? "Already scheduled" : "Auto-schedule now"}
                </button>
                <Link to="/dashboard/calendar" className="text-xs text-indigo-300 hover:underline">View on calendar →</Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {job && job.status !== "running" && (
        <section className="glass rounded-2xl p-6" data-testid="power-hour-last-job">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/40">Last run</div>
              <h3 className="font-heading text-xl font-black flex items-center gap-2 mt-1">
                {job.status === "completed" ? <Check className="h-4 w-4 text-emerald-400" /> : <Hourglass className="h-4 w-4 text-amber-400" />}
                {job.status}
              </h3>
            </div>
            <Link to="/admin/approvals" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg glass text-xs hover:bg-white/5">
              Open approval queue <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono text-white/60">
            <div>Posts: {progress.posts}</div>
            <div>Blogs: {progress.blogs}</div>
            <div>Images: {progress.images}</div>
            <div>Campaign: {progress.campaign}</div>
          </div>
        </section>
      )}

      {history.length > 1 && (
        <section data-testid="power-hour-history">
          <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">History</div>
          <div className="space-y-2">
            {history.slice(1).map((h) => (
              <div key={h.id} className="glass rounded-lg p-3 text-xs font-mono flex items-center justify-between gap-2">
                <span className="truncate">{h.id.slice(0, 8)}…</span>
                <span className="text-white/40">{new Date(h.started_at).toLocaleString()}</span>
                <span className={h.status === "completed" ? "text-emerald-300" : "text-amber-300"}>{h.status}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-[0.2em] text-white/40">{label}</span>
        <Icon className={`h-4 w-4 text-${accent}-400`} />
      </div>
      <div className="font-heading text-2xl font-black">{value}</div>
    </div>
  );
}
